import { getRegionState } from '@packages/stores';
import { AnalyticsCommonProps, PLATFORM } from '@packages/types';

import { AnalyticsBrowser } from '@segment/analytics-next';
import { captureException, setUser, withScope } from '@sentry/react';

const __DEV__ = process.env.NODE_ENV === 'development';
const analytics = AnalyticsBrowser.load({
  writeKey: import.meta.env.VITE_SEGMENT_WRITE_KEY,
});

/*
    By default Segment will only forward events to Mixpanel
    Enable event forwarding to other destinations by passing a custom options object
    when making a call to sendAnalyticsEvent
     */
const defaultSegmentOptions = {
  integrations: {
    All: true,
    Slack: false,
  },
};

export const sendAnalyticsEvent = (
  eventName: string,
  properties: { [key: string]: unknown } = {},
  options: { [key: string]: unknown } = {},
) => {
  withScope(scope => {
    scope.setTag('source', 'sendAnalyticsEvent');
    try {
      const region = getRegionState();

      const commonProps: AnalyticsCommonProps = {
        platform: PLATFORM.WIDGET,
        region: region?.country ?? '',
      };

      analytics
        .track(
          eventName,
          region && region.country ? { ...commonProps, ...properties } : { ...properties },
          { ...defaultSegmentOptions, ...options },
        )
        .catch(e => {
          throw e;
        });

      if (__DEV__) {
        console.log('Segment Track ========>', eventName);
        console.log('Segment Track Properties ========>', properties);
      }
    } catch (e) {
      captureException(e);
    }
  });
};

export const sendAnalyticsPage = (
  eventName: string,
  properties: { [key: string]: unknown } = {},
  options: { [key: string]: unknown } = {},
) => {
  withScope(scope => {
    scope.setTag('source', 'sendAnalyticsPage');
    try {
      const region = getRegionState();
      const commonProps: AnalyticsCommonProps = {
        platform: PLATFORM.WIDGET,
        region: region?.country ?? '',
      };
      analytics
        .page(
          eventName,
          region && region.country ? { ...commonProps, ...properties } : { ...properties },
          { ...defaultSegmentOptions, ...options },
        )
        .catch(e => {
          throw e;
        });

      if (__DEV__) {
        console.log('Segment Page ========>', eventName);
      }
    } catch (e) {
      captureException(e);
    }
  });
};

export const sendAnalyticsUserId = (
  userId: string | null,
  properties: { [key: string]: unknown } = {},
  options: { [key: string]: unknown } = defaultSegmentOptions,
) => {
  withScope(scope => {
    scope.setTag('source', 'sendAnalyticsUserId');
    try {
      const region = getRegionState();
      const commonProps: AnalyticsCommonProps = {
        platform: PLATFORM.WIDGET,
        region: region?.country ?? '',
        state: region?.state ?? '',
      };
      analytics
        .identify(
          userId,
          region && region.country ? { ...commonProps, ...properties } : { ...properties },
          options,
        )
        .catch(e => {
          throw e;
        });

      if (__DEV__) {
        console.log('Segment Identify =========>', userId);
      }
    } catch (e) {
      captureException(e);
    }
  });
};

export const sendAnalyticsECommerce = (
  eventName: string,
  properties: { [key: string]: unknown } = {},
) => {
  sendAnalyticsEvent(eventName, properties, {
    integrations: {
      All: true,
      Slack: false,
    },
  });
  if (__DEV__) {
    console.log('Segment ECommerce Track ========>', eventName);
  }
};

export const getAnonymousId = async () => {
  return await withScope(async scope => {
    scope.setTag('source', 'getAnonymousId');
    try {
      const anonymousId = (await analytics.user())?.anonymousId();
      if (anonymousId) {
        setUser({ id: anonymousId });
      }
      return anonymousId;
    } catch (e) {
      captureException(e);
    }
  });
};
